import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet"; 
import SEO from "./components/SEO";
import logo from "./assets/logo.png";

const Landing = () => {
  const [showContact, setShowContact] = useState(false);

  const shake = {
    scale: [1, 1.05, 0.95, 1.02, 1],
    transition: { duration: 0.4 },
  };

  const sparkles = Array.from({ length: 30 }, (_, i) => i);

  // 📈 Advanced SoftwareApplication Schema to maximize Search Console indexing velocity
  const platformSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "InvestorLanes Platforms",
    "operatingSystem": "All",
    "applicationCategory": "BusinessApplication",
    "description": "Connecting ambitious entrepreneurs with real, verified startup allocators across Africa.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <motion.div
      initial={{ x: "-100%" }}
      animate={{ x: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="min-h-screen flex flex-col relative overflow-hidden"
    >
      <SEO
        title="InvestorLanes – Connect with Investors & Get Startup Funding"
        description="InvestorLanes helps founders connect with verified investors across Africa. Secure funding, mentorship, and growth opportunities."
        canonicalUrl="https://investorlanes.com"
        image="https://investorlanes.com"
      />

      {/* Structured Core Schema Injection for elite ranking power */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(platformSchema)}
        </script>
      </Helmet>

      {/* 🌈 Gradient Background */}
      <div className="absolute inset-0 -z-20 bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 animate-gradient-x"></div>

      {/* ✨ Floating Sparkles */}
      {sparkles.map((i) => {
        const size = Math.random() * 2 + 1;
        const duration = 3 + Math.random() * 4;
        const delay = Math.random() * 5;
        const top = Math.random() * 100;
        const left = Math.random() * 100;
        return (
          <div
            key={i}
            className="absolute rounded-full bg-yellow-400"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              top: `${top}%`,
              left: `${left}%`,
              opacity: 0.7,
              animation: `twinkle ${duration}s ease-in-out ${delay}s infinite alternate`,
              boxShadow: "0 0 6px rgba(255, 215, 0, 0.8)",
            }}
          ></div>
        );
      })}

      {/* 🌍 Floating Glass Navbar */}
      <motion.header
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="sticky top-4 z-30 mx-auto w-[95%] md:w-[90%] flex items-center justify-between px-6 py-3 bg-white/40 backdrop-blur-xl shadow-lg rounded-2xl border border-white/30"
        style={{ backdropFilter: "blur(12px)" }}
      >
        <Link to="/" className="inline-block">
          <img
            src={logo}
            alt="InvestorLanes"
            title="InvestorLanes"
            className="w-auto h-auto max-w-[100px] max-h-[100px] drop-shadow-sm"
          />
        </Link>

        <div className="flex items-center gap-6">
          <Link
            to="/about"
            className="relative text-sm font-medium text-gray-800 hover:text-blue-700 transition group"
          >
            <span className="group-hover:text-blue-600 transition animate-pulse-glow">
              💎 About
            </span>
          </Link>

          <a
            href="mailto:support@investorlanes.com"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 transition hover:drop-shadow-[0_0_6px_rgba(37,99,235,0.6)]"
          >
            ✉️ Engage: support@investorlanes.com
          </a>
        </div>
      </motion.header>

      {/* 🌍 Main Content */}
      <main className="flex-1 grid place-items-center text-center px-6 z-10 relative mt-10">
        <div className="max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 tracking-tight">
            🌍 Connecting ambitious entrepreneurs with real, verified Investors.
          </h1>
          <p className="text-lg md:text-xl mb-8 text-gray-700 leading-relaxed">
            InvestorLanes bridges the gap between <strong>entrepreneurs </strong> and{" "}
            <strong>investors</strong> across Africa. Our AI-driven matchmaking helps startups secure funding faster than lightning while helping investors discover high-potential investment opportunities.
          </p>

          {/* 🔥 CTA Buttons */}
          <div className="flex flex-col gap-4 items-center mb-6">
            <motion.div whileTap={shake}>
              <Link
                to="/signup"
                className="inline-block bg-blue-500 text-white px-6 py-3 rounded-xl hover:bg-blue-600 transition shadow-md w-64 text-center font-semibold"
              >
                🔑 Get Started
              </Link>
            </motion.div>
            
            <motion.div whileTap={shake}>
              <Link
                to="/login"
                className="inline-block bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition shadow-md w-64 text-center font-semibold"
              >
                🔐 Log In
              </Link>
            </motion.div>
            
            <motion.div whileTap={shake}>
              <Link
                to="/browse-investors"
                className="inline-block bg-green-500 text-white px-6 py-3 rounded-xl hover:bg-green-600 transition shadow-md w-64 text-center font-semibold"
              >
                🌟 Browse Investors
              </Link>
            </motion.div>
          </div>

          <p className="text-gray-600 text-sm mb-12">
            🛡️ Trusted by entrepreneurs & investors worldwide. AI-powered, secure.
          </p>

          {/* 💼 Animated Motivation Section */}
          <motion.section
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            viewport={{ once: true }}
            className="my-20 bg-white/70 backdrop-blur-md rounded-3xl p-8 shadow-lg border border-white/30 hover:shadow-xl transition text-center"
          >
            <motion.h2
              animate={{ scale: [1, 1.05, 1], rotate: [0, 1, -1, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="text-3xl font-bold mb-4 text-gray-900"
            >
              💼 Keep It Moving
            </motion.h2>
            <p className="text-gray-700 leading-relaxed max-w-2xl mx-auto">
              Greatness waits for no one. In business and innovation, momentum is everything.
              Keep building, keep networking, and always keep it moving — the right opportunity is one click away.
            </p>
          </motion.section>
          {/* 📬 Contact Section */}
          <section className="my-16 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 p-8 rounded-3xl shadow-lg text-center relative overflow-hidden">
            <h2
              className="text-3xl font-bold mb-6 cursor-pointer hover:text-blue-600 transition relative z-10"
              onClick={() => setShowContact(!showContact)}
            >
              {showContact ? "📬 Hide Contact Info" : "📬 Contact Us"}
            </h2>

            <AnimatePresence>
              {showContact && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -20 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -20 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="overflow-hidden relative z-10"
                >
                  <div className="bg-white/60 backdrop-blur-md p-6 rounded-2xl inline-block border border-white/30 shadow-md">
                    <p className="text-gray-800 font-medium text-lg">
                      📩 Email us now at:{" "}
                      <a
                        href="mailto:support@investorlanes.com"
                        className="text-blue-600 hover:text-blue-800 font-semibold transition"
                      >
                        support@investorlanes.com
                      </a>
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        </div>
      </main>

      {/* ⚜️ Centered Footer (Legally fortified for automated gateway review) */}
      <motion.footer
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
        className="w-full py-8 mt-auto bg-white/95 backdrop-blur border-t border-gray-100 shadow-inner z-10 relative flex justify-center items-center"
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          {/* Paystack Required Dynamic Verification Nodes */}
          <div className="mb-6 text-xs text-gray-500 space-y-2 max-w-2xl mx-auto leading-relaxed">
            <p>
              InvestorLanes is a platform connecting entrepreneurs with investors.
            </p>
            <p>
              All payments are securely processed through <strong>trusted brands</strong>.
            </p>
            <p className="text-gray-400">
              By using this website you agree to our{" "}
              <Link to="/terms-of-service" className="text-blue-600 hover:underline font-medium mx-0.5">Terms of Service</Link>,{" "}
              <Link to="/privacy-policy" className="text-blue-600 hover:underline font-medium mx-0.5">Privacy Policy</Link>,{" "}
              <Link to="/refund-policy" className="text-blue-600 hover:underline font-medium mx-0.5">Cancellation & Refund Policy</Link>, and{" "}
              <Link to="/cookie-policy" className="text-blue-600 hover:underline font-medium mx-0.5">Cookie Policy</Link>.
            </p>
          </div>

          <p className="text-gray-400 text-xs font-normal tracking-wide">
            © 2026 InvestorLanes.com — All Rights Reserved. Headquartered in Midrand, Johannesburg, South Africa.
          </p>
        </div>
      </motion.footer>

      {/* 🌠 Animations */}
      <style>
        {`
          @keyframes gradient-x {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .animate-gradient-x { background-size: 200% 200%; animation: gradient-x 15s ease infinite; }

          @keyframes twinkle {
            0% { opacity: 0.3; transform: scale(0.5); }
            50% { opacity: 1; transform: scale(1); }
            100% { opacity: 0.3; transform: scale(0.5); }
          }

          @keyframes pulseGlow {
            0%, 100% { text-shadow: 0 0 6px rgba(59,130,246,0.3), 0 0 12px rgba(147,197,253,0.4); }
            50% { text-shadow: 0 0 12px rgba(59,130,246,0.6), 0 0 24px rgba(147,197,253,0.7); }
          }

          .animate-pulse-glow {
            animation: pulseGlow 4s ease-in-out infinite;
          }
        `}
      </style>
    </motion.div>
  );
};

export default Landing;
